import { cn } from "@/lib/utils";
import React, { FC } from "react";
import { Booking } from "@/types/booking";
import { useTranslation } from "react-i18next";
import { RequestStatus } from "@/types/request";
import { Reservation } from "@/types/reservation";
import ReactCountryFlag from "react-country-flag";
import { formatGuestString } from "@/lib/booking-utils";
import { determineGuestStatus } from "@/components/pages/reservations/guests/utils/guestStatusUtils";
import { useAccessCapabilities } from "@/hooks/useAccessCapabilities";

interface GeneralBookingInfoProps {
  booking: Reservation;
  showRoomInfo?: boolean;
  className?: string;
}

export const GeneralBookingInfo: FC<GeneralBookingInfoProps> = ({
  booking,
  showRoomInfo = true,
  className,
}) => {
  const { t } = useTranslation();
  const { capabilities } = useAccessCapabilities();
  const info = [
    {
      key: formatGuestString(booking.additionalGuests),
      value: (
        <span className="break-words">
          {booking?.additionalGuests
            ?.map((g) =>
              `${g.guest?.profile?.firstName ?? ""} ${g.guest?.profile?.lastName ?? ""}`.trim(),
            )
            .filter(Boolean)
            .join(", ")}
        </span>
      ),
    },
    ...(showRoomInfo
      ? [
          {
            key: t("chats.room"),
            value: booking.room?.number,
          },
          {
            key: t("chats.category"),
            value: booking.room?.category?.name,
          },
        ]
      : []),
    {
      key: t("chats.preferredLanguage"),
      value: booking.guest.profile.language ? (
        <ReactCountryFlag
          countryCode={booking.guest.profile.language}
          svg
          className={"!h-5 !w-7 rounded-sm"}
        />
      ) : (
        t("chats.notSpecified")
      ),
    },
    {
      key: t("chats.reservationStatus"),
      value: determineGuestStatus(booking as unknown as Booking),
    },
    ...(capabilities.canUseRequestsSystem
      ? [
          {
            key: t("chats.roomServiceStatus"),
            value: booking?.serviceRequests?.every((r) =>
              r.tasks?.every((task) => task.status === RequestStatus.DONE),
            )
              ? t("chats.completed")
              : t("chats.pending"),
          },
        ]
      : []),
  ];
  return (
    <div className={cn("flex flex-col gap-2", className)}>
      {info.map(({ key, value }) => (
        <div
          key={key}
          className="flex min-w-0 flex-row items-center justify-between gap-3 text-sm"
        >
          <span className="shrink-0 text-neutral-500">{key}</span>
          <span className="text-eathy-comfort-1 min-w-0 text-right font-medium break-words">
            {value}
          </span>
        </div>
      ))}
    </div>
  );
};
